<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import { onDestroy } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableFilterValues,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import { closeOverlayById, openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { spaNavigate } from "@kontextwerk/web-sdk/core"
  import { mdiPlus } from "@mdi/js"
  import type { OrganizationFrame, RelationshipStatus } from "@kontextwerk/control-web-client"
  import type { PermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
  import {
    kehrblattFrameMembers,
    resolveFrameMemberAvatarUrl,
  } from "../../../../../../../../../../api/kehrblatt/organization-frame"
  import type { UsersDirectory } from "../../../../_shared/_logic/users-directory"
  import { buildRoleIndex, findRole } from "../../../../_shared/_logic/role-presentation"
  import UsersTableActions, {
    OPEN_USER_ACTION_ID,
    RESEND_INVITATION_ACTION_ID,
  } from "./_actions/UsersTableActions.actions"
  import { changeMembershipAccess } from "./_actions/membershipAccess"
  import { resendInvitation, revokeInvitation } from "./_actions/invitationCommands"
  import UsersTableCell from "./_modules/users-table-cell"
  import UserCreateOverlay from "./_modules/user-create-overlay"
  import {
    buildUserColumns,
    buildUserRows,
    filterUserRows,
    userRowsSignature,
    type InvitationRow,
    type MemberRow,
    type UserRow,
  } from "./_shared/_logic/table"

  interface Props {
    organization: OrganizationFrame
    directory: UsersDirectory
    permissions: PermissionState
    currentMembershipId: string | null
    /** Re-reads the organization roster after a confirmed membership change. */
    onMembersChanged: () => unknown
  }

  let { organization, directory, permissions, currentMembershipId, onMembersChanged }: Props =
    $props()

  const canManage = $derived(permissions.allows("core.memberships", "manage"))
  const canViewRoles = $derived(permissions.allows("core.roles", "view"))
  const roleIndex = $derived(
    buildRoleIndex({
      roles: directory.roles,
      choices: directory.choices,
      ownRole: directory.actor
        ? { id: directory.actor.roleId, name: directory.actor.roleName }
        : null,
    }),
  )
  const actorIsOwner = $derived(
    Boolean(
      directory.actor &&
      !directory.actor.delegated &&
      findRole(roleIndex, directory.actor.roleId)?.kind === "owner",
    ),
  )
  const rows = $derived<UserRow[]>(
    buildUserRows({
      members: kehrblattFrameMembers(organization),
      invitations: directory.invitations,
      roles: roleIndex,
      currentMembershipId,
      avatarUrl: resolveFrameMemberAvatarUrl,
    }),
  )
  const columns = $derived<ColumnDef[]>(buildUserColumns(rows.some((row) => row.email)))
  const loading = $derived(
    [directory.rolesState, directory.choicesState, directory.invitationsState].includes("loading"),
  )
  const refreshKey = $derived(userRowsSignature(rows))
  const inviteDisabledReason = $derived(
    directory.choicesState === "failed"
      ? "Die zuweisbaren Rollen konnten nicht geladen werden."
      : directory.choicesState === "ready" && !directory.choices.length
        ? "Es gibt keine Rolle, die Sie zuweisen dürfen."
        : null,
  )
  const canInvite = $derived(
    canManage && directory.choicesState === "ready" && directory.choices.length > 0,
  )

  let pendingRowIds = $state<ReadonlySet<string>>(new Set())
  let inviteOverlayId: string | null = null
  let destroyed = false

  const reloadMembers = async () => {
    try {
      await onMembersChanged()
    } catch {
      if (!destroyed)
        newNotification({
          class: "error",
          html: "Die Benutzerliste konnte nicht aktualisiert werden.",
        })
    }
  }

  const setPending = (rowId: string, pending: boolean) => {
    const next = new Set(pendingRowIds)

    if (pending) next.add(rowId)
    else next.delete(rowId)

    pendingRowIds = next
  }

  const getRows: TableGetRows<TableFilterValues> = async (params) => {
    const { filters, limit, offset } = params
    const sort =
      (params as { sort?: { columnId: string; key: string; dir: "asc" | "desc" } | null }).sort ??
      null
    // Role labels and invitation rows belong to the first rendering; no interim "—" flicker.
    await directory.load()
    const filtered = filterUserRows(rows, String(filters?.email ?? ""))

    return getSortedTablePage({ rows: filtered, columns, sort, limit, offset })
  }

  const openUserPage = (row: MemberRow) => {
    const organizationSegment = encodeURIComponent(organization.slug || organization.id)

    spaNavigate(`/organizations/${organizationSegment}/users/${encodeURIComponent(row.userId)}`)
  }

  const changeAccess = async (row: MemberRow, status: RelationshipStatus) => {
    const api = directory.access

    if (!api || pendingRowIds.has(row.id) || !directory.current()) return

    setPending(row.id, true)
    try {
      const result = await changeMembershipAccess({ api, member: row, status })

      if (destroyed) return

      if (result.status === "done") {
        newNotification({
          class: "success",
          html: status === "suspended" ? "Benutzer wurde gesperrt." : "Benutzer wurde entsperrt.",
        })
        await reloadMembers()
      } else if (result.status === "conflict") {
        newNotification({
          class: "info",
          html: "Der Benutzer wurde zwischenzeitlich geändert. Die Liste wurde neu geladen.",
        })
        await reloadMembers()
      } else if (result.status === "forbidden") {
        newNotification({ class: "error", html: "Für diese Änderung fehlt die Berechtigung." })
      } else if (result.status === "failed") {
        newNotification({ class: "error", html: "Benutzer konnte nicht geändert werden." })
      }
    } finally {
      if (!destroyed) setPending(row.id, false)
    }
  }

  const runInvitationCommand = async (
    row: InvitationRow,
    command: typeof resendInvitation,
    success: string,
  ) => {
    const api = directory.invitationsApi

    if (!api || pendingRowIds.has(row.id) || !directory.current()) return

    setPending(row.id, true)
    try {
      const result = await command({ api, invitation: row })

      if (destroyed) return

      if (result.status === "done") {
        newNotification({ class: "success", html: success })
        await directory.reloadInvitations()
      } else if (result.status === "conflict") {
        newNotification({
          class: "info",
          html: "Die Einladung ist nicht mehr offen. Die Liste wurde neu geladen.",
        })
        await directory.reloadInvitations()
      } else if (result.status === "forbidden") {
        newNotification({ class: "error", html: "Für diese Änderung fehlt die Berechtigung." })
      } else if (result.status === "failed") {
        newNotification({ class: "error", html: "Einladung konnte nicht geändert werden." })
      }
    } finally {
      if (!destroyed) setPending(row.id, false)
    }
  }

  const closeInviteOverlay = () => {
    if (inviteOverlayId) closeOverlayById(inviteOverlayId)

    inviteOverlayId = null
  }

  const openCreateOverlay = () => {
    const api = directory.invitationsApi

    if (!canInvite || !api || inviteOverlayId || !directory.current()) return

    const id = openOverlay({
      title: "Benutzer einladen",
      content: UserCreateOverlay,
      bodyPadding: "none",
      properties: {
        choices: directory.choices,
        invitations: api,
        access: directory.access,
        canViewRoles,
        onInvited: () => directory.reloadInvitations(),
      },
      onClose: () => {
        if (inviteOverlayId === id) inviteOverlayId = null
      },
    })
    inviteOverlayId = id
  }

  // effect-ok: external-runtime - a confirmed-context change closes this owner's invitation dialog.
  $effect(() => {
    if (!directory.available) closeInviteOverlay()
  })

  onDestroy(() => {
    destroyed = true
    closeInviteOverlay()
  })
</script>

{#snippet usersToolbarSnippet()}
  {#if canManage}
    <CTA
      type="primaryButton"
      content=""
      ariaLabel="Benutzer einladen"
      tooltipContent={inviteDisabledReason ?? "Benutzer einladen"}
      onClick={openCreateOverlay}
      disabled={!canInvite}
      dataTestid="controlAdmin.users.inviteButton"
    >
      {#snippet iconSnippet()}
        <Icon path={mdiPlus} />
      {/snippet}
    </CTA>
  {/if}
{/snippet}

{#snippet usersTableCell({ row, column }: { row: DataRow; column: ColumnDef })}
  <UsersTableCell row={row as UserRow} {column} />
{/snippet}

{#snippet usersEmpty()}
  <p class="hint">Keine Benutzer gefunden.</p>
{/snippet}

<div class="users-table" data-testid="controlAdmin.users.table">
  {#if directory.invitationsState === "failed"}
    <div class="state-row" role="alert">
      <p class="hint">Offene Einladungen konnten nicht geladen werden.</p>
      <CTA
        type="secondaryButton"
        content="Erneut versuchen"
        onClick={() => void directory.reloadInvitations()}
      />
    </div>
  {/if}
  <Table
    titleRow={{
      content: "Benutzer",
      brightBackground: true,
      showFormatButtons: true,
      buttonSnippet: usersToolbarSnippet,
    }}
    {columns}
    {getRows}
    getRowId={(row) => String(row.id)}
    urlStateId={`org-users-${organization.id}`}
    {refreshKey}
    {loading}
    theme="light"
    mode="table"
    showActions={true}
    actionsHeaderLabel="Aktionen"
    filter={{
      column: "email",
      type: "text",
      label: "Nach Benutzer filtern…",
      placeholder: "Nach Benutzer filtern…",
    }}
    tableCell={usersTableCell}
    rowActions={{
      resolve: (row) => {
        const userRow = row as UserRow

        return buildTableRowActionSet(
          UsersTableActions({
            row: userRow,
            canManage,
            actorIsOwner,
            pendingRowIds,
            onOpenUserPage: openUserPage,
            onChangeAccess: (member, status) => void changeAccess(member, status),
            onResendInvitation: (invitation) =>
              void runInvitationCommand(
                invitation,
                resendInvitation,
                "Einladung wurde erneut versendet.",
              ),
            onRevokeInvitation: (invitation) =>
              void runInvitationCommand(
                invitation,
                revokeInvitation,
                "Einladung wurde zurückgezogen.",
              ),
          }),
          {
            primaryId: userRow.kind === "member" ? OPEN_USER_ACTION_ID : null,
            inlineIds: [RESEND_INVITATION_ACTION_ID],
          },
        )
      },
    }}
    empty={usersEmpty}
  />
</div>

<style lang="less">
  .users-table {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    min-width: 0;
  }

  .state-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
  }

  .hint {
    margin: 0;
    color: var(--text-invers-100);
  }
</style>
